"use client";

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authApi } from "@/lib/api-client";

// ============ 类型定义 ============

export interface UserInfo {
  id: string;
  tenantId: string;
  username: string;
  displayName: string;
  email: string;
  phone: string;
  avatar: string;
  status: string;
  deptId?: string;
  position?: string;
  lastLoginAt?: string;
  lastLoginIp?: string;
  mustChangePassword?: boolean;
}

export interface TenantInfo {
  id: string;
  name: string;
  code: string;
  modelType: string;
  status: string;
}

export interface AvailableTenant {
  id: string;
  name: string;
  code: string;
  modelType: string;
  status: string;
  role: string;
  isCurrent: boolean;
}

interface AuthState {
  user: UserInfo | null;
  tenant: TenantInfo | null;
  availableTenants: AvailableTenant[];
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
}

interface AuthContextValue extends AuthState {
  login: (tenantId: string, username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: string) => void;
  updateUser: (updates: Partial<UserInfo>) => void;
}

// ============ Mock 数据（后端不可用时降级） ============

const mockAvailableTenants: AvailableTenant[] = [
  { id: "sys-admin", name: "系统管理", code: "SYS", modelType: "STANDARD", status: "active", role: "超级管理员", isCurrent: true },
  { id: "huawei-group", name: "华为集团", code: "HW", modelType: "GROUP", status: "active", role: "集团管理员", isCurrent: false },
  { id: "china-telecom", name: "中国电信", code: "CT", modelType: "CHANNEL", status: "active", role: "渠道管理员", isCurrent: false },
  { id: "tech-startup", name: "科技创新公司", code: "TECH", modelType: "STANDARD", status: "active", role: "租户管理员", isCurrent: false },
  { id: "edu-org", name: "教育机构", code: "EDU", modelType: "COMMUNITY", status: "active", role: "社团管理员", isCurrent: false },
  { id: "freelancer", name: "自由开发者", code: "FREE", modelType: "PERSONAL", status: "active", role: "个人用户", isCurrent: false },
];

// ============ Context ============

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    tenant: null,
    availableTenants: mockAvailableTenants,
    isAuthenticated: false,
    isLoading: true,
    token: null,
  });

  // 初始化：检查本地 token
  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem("ts_token");
      const savedUser = localStorage.getItem("ts_user");
      const savedTenant = localStorage.getItem("ts_tenant");

      if (token && savedUser && savedTenant) {
        try {
          // 尝试从后端验证 token
          const res = await authApi.info();
          if (res.code === 200 && res.data?.user) {
            const userData = res.data.user as Record<string, unknown>;
            const tenantData = res.data.tenant as Record<string, unknown>;
            setState(prev => ({
              ...prev,
              user: {
                id: String(userData.id || ""),
                tenantId: String(userData.tenantId || ""),
                username: String(userData.username || ""),
                displayName: String(userData.displayName || userData.display_name || ""),
                email: String(userData.email || ""),
                phone: String(userData.phone || ""),
                avatar: String(userData.avatar || ""),
                status: String(userData.status || "active"),
              },
              tenant: {
                id: String(tenantData.id || ""),
                name: String(tenantData.name || ""),
                code: String(tenantData.code || ""),
                modelType: String(tenantData.modelType || tenantData.model_type || "STANDARD"),
                status: String(tenantData.status || "active"),
              },
              isAuthenticated: true,
              isLoading: false,
              token,
            }));
            return;
          }
        } catch {
          // 后端不可用，使用本地缓存
          try {
            const user = JSON.parse(savedUser) as UserInfo;
            const tenant = JSON.parse(savedTenant) as TenantInfo;
            setState(prev => ({
              ...prev,
              user,
              tenant,
              isAuthenticated: true,
              isLoading: false,
              token,
            }));
            // 设置 cookie 供 middleware 使用
            document.cookie = `tianshu_token=${token}; path=/; max-age=86400; SameSite=Lax`;
            return;
          } catch {
            // 缓存损坏，清除
            localStorage.removeItem("ts_token");
            localStorage.removeItem("ts_user");
            localStorage.removeItem("ts_tenant");
          }
        }
      }
      setState(prev => ({ ...prev, isLoading: false }));
    };
    init();
  }, []);

  const login = useCallback(async (tenantId: string, username: string, password: string) => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const res = await authApi.login(tenantId, username, password);
      if (res.code === 200 && res.data?.user) {
        const { token, user: userData, tenant: tenantDataRaw } = res.data as {
          token: string;
          user: Record<string, unknown>;
          tenant?: Record<string, unknown>;
        };
        const tenantData = tenantDataRaw || {};

        const user: UserInfo = {
          id: String(userData.id || ""),
          tenantId: String(userData.tenantId || userData.tenant_id || tenantId),
          username: String(userData.username || username),
          displayName: String(userData.displayName || userData.display_name || ""),
          email: String(userData.email || ""),
          phone: String(userData.phone || ""),
          avatar: String(userData.avatar || ""),
          status: String(userData.status || "active"),
        };

        const tenant: TenantInfo = {
          id: String(tenantData.id || tenantId),
          name: String(tenantData.name || ""),
          code: String(tenantData.code || ""),
          modelType: String(tenantData.modelType || tenantData.model_type || "STANDARD"),
          status: String(tenantData.status || "active"),
        };

        localStorage.setItem("ts_token", token);
        localStorage.setItem("ts_user", JSON.stringify(user));
        localStorage.setItem("ts_tenant", JSON.stringify(tenant));
        // 设置 cookie 供 middleware 使用
        document.cookie = `tianshu_token=${token}; path=/; max-age=86400; SameSite=Lax`;

        setState(prev => ({
          ...prev,
          user,
          tenant,
          isAuthenticated: true,
          isLoading: false,
          token,
        }));
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
        throw new Error(res.msg || "登录失败");
      }
    } catch (err) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // 忽略后端错误
    }
    localStorage.removeItem("ts_token");
    localStorage.removeItem("ts_user");
    localStorage.removeItem("ts_tenant");
    // 清除 cookie
    document.cookie = "tianshu_token=; path=/; max-age=0";
    setState(prev => ({
      ...prev,
      user: null,
      tenant: null,
      isAuthenticated: false,
      token: null,
    }));
  }, []);

  const switchTenant = useCallback((tenantId: string) => {
    setState(prev => {
      const target = prev.availableTenants.find(t => t.id === tenantId);
      if (!target) return prev;

      const newTenant: TenantInfo = {
        id: target.id,
        name: target.name,
        code: target.code,
        modelType: target.modelType,
        status: target.status,
      };

      const newTenants = prev.availableTenants.map(t => ({
        ...t,
        isCurrent: t.id === tenantId,
      }));

      localStorage.setItem("ts_tenant", JSON.stringify(newTenant));

      return {
        ...prev,
        tenant: newTenant,
        availableTenants: newTenants,
      };
    });
  }, []);

  const updateUser = useCallback((updates: Partial<UserInfo>) => {
    setState(prev => {
      if (!prev.user) return prev;
      const newUser = { ...prev.user, ...updates };
      localStorage.setItem("ts_user", JSON.stringify(newUser));
      return { ...prev, user: newUser };
    });
  }, []);

  return (
    <AuthContext.Provider value={{
      ...state,
      login,
      logout,
      switchTenant,
      updateUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
